<script lang="ts">
    import { Heading, P, Tooltip } from "flowbite-svelte";
    import posthog from "posthog-js";
    import ResetCounterModal from "./ResetCounterModal.svelte";
    import { State } from "../state.svelte";
    import ColoredButton from "../colored/ColoredButton.svelte";
    import { DateTime } from "luxon";
    import { Helper } from "../helpers";
    import CircleMinus from "../icons/CircleMinus.svelte";
    import CirclePlus from "../icons/CirclePlus.svelte";
    import { onDestroy, onMount } from "svelte";

    const buttonWidth = "w-[10rem]";

    let lastUpdatedTitle: string = $derived(DateTime.fromISO(State.lastUpdated).toFormat("HH:mm:ss MM/dd/yyyy"));

    let resetModal: boolean = $state(false);

    onMount(registerShortcutListner);
    onDestroy(unregisterShortcutListner);

    function increment() {
        State.count += 1;
        Helper.updateCounterTime();
        posthog.capture('counter_incremented', { new_count: State.count });
    }

    function decrement() {
        if (State.count > 0) {
            State.count -= 1;
            Helper.updateCounterTime();
            posthog.capture('counter_decremented', { new_count: State.count });
        }
    }

    function openDeleteModal() {
        resetModal = true;
    }

    function closeDeleteModal() {
        resetModal = false;
    }

    function handleKeyDown(event: KeyboardEvent) {
        //* String is a single space because that's how the space key is represented in JS
        if (event.key === " ") {
            event.preventDefault();
            increment();
        }

        if (event.key === "Backspace") {
            event.preventDefault();
            decrement();
        }
    }

    function registerShortcutListner() {
        if (State.options.keyboardShortcuts)
            document.addEventListener("keydown", handleKeyDown);
    }

    function unregisterShortcutListner() {
        if (State.options.keyboardShortcuts)
            document.removeEventListener("keydown", handleKeyDown);
    }

    $effect(() => {
        if (State.options.keyboardShortcuts) {
            unregisterShortcutListner();
            registerShortcutListner();
        }
    });

</script>

{#snippet minusButton()}
    <ColoredButton color="lavender" onclick={decrement}>
        <CircleMinus/>
    </ColoredButton>
{/snippet}

{#snippet plusButton()}
    <ColoredButton color="lavender" onclick={increment}>
        <CirclePlus/>
    </ColoredButton>
{/snippet}

{#snippet resetButton(text = "Reset Counter", width = buttonWidth)}
    <ColoredButton color="red" class="{width} overflow-hidden" onclick={openDeleteModal}>
        {text}
    </ColoredButton>
{/snippet}

<ResetCounterModal bind:isOpen={resetModal} close={closeDeleteModal} reset={Helper.resetCounter}/>

<div class="flex flex-col items-center justify-center w-full h-full relative px-5">
    <Heading
            class="text-5xl v4xs:!text-6xl font-bold flex items-center justify-center overflow-hidden black:text-white frappe:text-ctp-pink"
            tag="h2">
        {State.count}
    </Heading>

    <div class="flex flex-col items-center justify-center -mt-5 vxs:-mt-10 vxs:pt-5">
        <P class="mt-4 black:text-gray-200 frappe:text-ctp-subtext0 text-xs v4xs:text-sm vxs:text-base">
            Last
            updated {State.lastUpdatedString}</P>
        <Tooltip>{lastUpdatedTitle}</Tooltip>
        <div class="hidden v3xs:!flex mt-2 lg:mt-0 items-center justify-between {buttonWidth} py-1">
            {@render minusButton()}

            {@render plusButton()}
        </div>
    </div>

    <div class="hidden v3xs:!flex mt-0 vxs:mt-2 items-center justify-center px-2 py-1 gap-2">
        {@render resetButton()}
    </div>

    <div class="flex v3xs:!hidden mt-0 vxs:mt-2 items-center justify-center px-2 py-1 gap-2">
        {@render minusButton()}
        {@render resetButton("Reset", "w-auto")}
        {@render plusButton()}
    </div>

</div>
